import { observer } from 'mobx-react-lite';
import {
  Box,
  CounterBadge,
  Heading,
  InfoIcon,
  InfoIconProps,
  Text,
  TextButton,
} from '@wix/design-system';
import React, { ReactNode } from 'react';
import { FiltersMap, useWixPatternsContainer } from '../..';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';

export interface ToolbarTitleProps {
  /**
   * Display a title at the left of the table toolbar.
   * @external
   */
  title: ReactNode;
  /**
   * Display a subtitle at the left of the table toolbar under the title.
   * @param {string} text The subtitle text
   * @param {[InfoIconProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form--infoicon)} info info to display an info icon next to the subtitle text
   * @param {string} learnMore an object with a `url`(required) and a `label`(optional), for displaying a link button on the subtitle
   * @external
   */
  subtitle?: {
    text: string;
    info?: InfoIconProps;
    learnMore?: {
      url: string;
      label?: string;
    };
  };
  /**
   * Indicates whether to show a total counter next to the table title. Please make sure to hide the counter in the page title (see hideTotal in [CollectionPage](/pages/cairo/?path=/story/components-page--collectionpage))
   * @external
   */
  showTotal?: boolean;
  /**
   * Shows the limit of items. Will bw shown when showTotal=true
   * @external
   */
  itemsLimit?: number;
}

export const ToolbarTitle = observer(function ToolbarTitle<
  T,
  F extends FiltersMap = FiltersMap,
>({ title, subtitle, showTotal, itemsLimit }: ToolbarTitleProps) {
  const { translate: t } = useWixPatternsContainer();
  const {
    collection: {
      result: { total, status },
    },
  } = useToolbarCollectionContext<T, F>();

  return (
    <Box direction="vertical" paddingTop="SP2" paddingBottom="SP2" width="100%">
      <Box minWidth={0} verticalAlign="middle" gap="SP1">
        <Heading dataHook="toolbar-title" ellipsis size="medium">
          {title}
        </Heading>
        {showTotal && (total || status.isSuccess) && (
          <CounterBadge
            size="small"
            skin="neutralLight"
            dataHook="toolbar-counter"
          >
            <>{itemsLimit === undefined ? total : `${total} / ${itemsLimit}`}</>
          </CounterBadge>
        )}
      </Box>
      <Box gap="SP1" verticalAlign="middle">
        <Text
          dataHook="toolbar-subtitle"
          size="medium"
          weight="thin"
          secondary
          ellipsis
        >
          {subtitle?.text}
        </Text>
        {subtitle?.info && <InfoIcon {...subtitle.info} size="small" />}
        {subtitle?.learnMore && (
          <TextButton
            dataHook="toolbar-subtitle-learn-more-cta"
            as="a"
            rel="noopener"
            target="_blank"
            href={subtitle.learnMore.url}
          >
            {subtitle.learnMore.label || t('cairo.table.subtitle.learnMore')}
          </TextButton>
        )}
      </Box>
    </Box>
  );
});
